import React, { useState } from 'react';
import axios from 'axios';
import { API } from '../../App';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../ui/dialog';
import { Calendar, Trash2, Edit, Play, Clock, Archive, Image } from 'lucide-react';
import { toast } from 'sonner';
import FileUpload from '../FileUpload';
import VideoUpload from '../VideoUpload';
import MultiLanguageInput from '../MultiLanguageInput';
import AutoTranslatedText from '../AutoTranslatedText';
import { useTranslation } from 'react-i18next';
import { parseLocalDate } from '../../utils/dateUtils';

const defaultEventForm = () => ({
  name: '', description: '', name_translations: {}, description_translations: {},
  start_date: '', end_date: '', buyer_fee: 0,
  booth_fees: { basic: 500, premium: 1500, elite: 3000 }, max_booths: 100,
  banner_url: '', intro_video_url: '', intro_video_url_fr: '', intro_video_url_es: '', intro_video_url_de: '',
  attendee_list_price: 0, exhibitor_list_price: 0,
  use_tiered_pricing: false,
  tiered_pricing: {
    attendee_list: [{ max: 100, price: 50 }, { max: 500, price: 100 }, { max: null, price: 200 }],
    exhibitor_list: [{ max: 100, price: 25 }, { max: 500, price: 50 }, { max: null, price: 100 }]
  },
  early_bird_enabled: false, early_bird_discount_percent: 15, early_bird_days_before: 30,
  region: '',
  coupon_enabled: false, coupon_code: '', coupon_discount_percent: 0, coupon_expiration_date: '',
  coupon_applies_to: 'both', coupon_max_uses: null, coupon_uses_count: 0,
  show_registration_stats: false, attendee_threshold: 50, exhibitor_threshold: 20,
  allow_exhibitor_registration_during_live: false
});

const AdminEventsTab = ({ events, fetchDashboardData, showCreateModal, setShowCreateModal }) => {
  const { t } = useTranslation();
  const [showEditEventModal, setShowEditEventModal] = useState(false);
  const [showDeleteEventConfirm, setShowDeleteEventConfirm] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [eventToDelete, setEventToDelete] = useState(null);
  const [eventForm, setEventForm] = useState(defaultEventForm());

  const resetEventForm = () => setEventForm(defaultEventForm());

  const openEditEvent = (event) => {
    setSelectedEvent(event);
    setEventForm({
      name: event.name, description: event.description,
      name_translations: event.name_translations || {}, description_translations: event.description_translations || {},
      start_date: event.start_date?.split('T')[0] || event.start_date,
      end_date: event.end_date?.split('T')[0] || event.end_date,
      buyer_fee: event.buyer_fee || 0,
      booth_fees: event.booth_fees || { basic: 500, premium: 1500, elite: 3000 },
      max_booths: event.max_booths || 100, banner_url: event.banner_url || '',
      intro_video_url: event.intro_video_url || '', intro_video_url_fr: event.intro_video_url_fr || '',
      intro_video_url_es: event.intro_video_url_es || '', intro_video_url_de: event.intro_video_url_de || '',
      attendee_list_price: event.attendee_list_price || 0, exhibitor_list_price: event.exhibitor_list_price || 0,
      use_tiered_pricing: event.use_tiered_pricing || false,
      tiered_pricing: event.tiered_pricing || defaultEventForm().tiered_pricing,
      early_bird_enabled: event.early_bird_enabled || false,
      early_bird_discount_percent: event.early_bird_discount_percent || 15,
      early_bird_days_before: event.early_bird_days_before || 30,
      region: event.region || '',
      coupon_enabled: event.coupon_enabled || false, coupon_code: event.coupon_code || '',
      coupon_discount_percent: event.coupon_discount_percent || 0,
      coupon_expiration_date: event.coupon_expiration_date?.split('T')[0] || '',
      coupon_applies_to: event.coupon_applies_to || 'both',
      coupon_max_uses: event.coupon_max_uses || null, coupon_uses_count: event.coupon_uses_count || 0,
      show_registration_stats: event.show_registration_stats || false,
      attendee_threshold: event.attendee_threshold || 50, exhibitor_threshold: event.exhibitor_threshold || 20,
      allow_exhibitor_registration_during_live: event.allow_exhibitor_registration_during_live || false
    });
    setShowEditEventModal(true);
  };

  const createEvent = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/events`, eventForm);
      toast.success('Event created successfully!');
      setShowCreateModal(false);
      resetEventForm();
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create event');
    }
  };

  const updateEvent = async (e) => {
    e.preventDefault();
    try {
      await axios.put(`${API}/events/${selectedEvent.id}`, eventForm);
      toast.success('Event updated successfully!');
      setShowEditEventModal(false);
      setSelectedEvent(null);
      resetEventForm();
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update event');
    }
  };

  const deleteEvent = async () => {
    if (!eventToDelete) return;
    try {
      await axios.delete(`${API}/events/${eventToDelete.id}`);
      toast.success('Event deleted successfully!');
      setShowDeleteEventConfirm(false);
      setEventToDelete(null);
      fetchDashboardData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete event');
    }
  };

  const updateEventStatus = async (eventId, status) => {
    try {
      await axios.put(`${API}/events/${eventId}/status?status=${status}`);
      toast.success(`Event status updated to ${status}!`);
      fetchDashboardData();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const getStatusBadge = (status) => {
    const configs = {
      live: { bg: 'bg-green-100 text-green-700', icon: Play, label: 'LIVE' },
      registration_open: { bg: 'bg-amber-100 text-amber-700', icon: Clock, label: 'REGISTRATION OPEN' },
      upcoming: { bg: 'bg-blue-100 text-blue-700', icon: Clock, label: 'UPCOMING' },
      completed: { bg: 'bg-slate-100 text-slate-700', icon: Archive, label: 'COMPLETED' },
      past: { bg: 'bg-slate-100 text-slate-700', icon: Archive, label: 'PAST' }
    };
    const config = configs[status] || configs.upcoming;
    const Icon = config.icon;
    return (
      <span className={`inline-flex items-center px-2 py-0.5 text-xs rounded ${config.bg}`}>
        <Icon className="h-3 w-3 mr-1" />
        {config.label}
      </span>
    );
  };

  const renderEventForm = (onSubmit, submitLabel, eventId = null) => (
    <form onSubmit={onSubmit} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
      <MultiLanguageInput
        label="Event Name" defaultValue={eventForm.name} translations={eventForm.name_translations}
        onDefaultChange={(value) => setEventForm({ ...eventForm, name: value })}
        onTranslationsChange={(translations) => setEventForm({ ...eventForm, name_translations: translations })}
        placeholder="Enter event name" required
      />
      <MultiLanguageInput
        label="Description" defaultValue={eventForm.description} translations={eventForm.description_translations}
        onDefaultChange={(value) => setEventForm({ ...eventForm, description: value })}
        onTranslationsChange={(translations) => setEventForm({ ...eventForm, description_translations: translations })}
        placeholder="Enter event description" multiline required
      />
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Start Date</Label>
          <Input type="date" value={eventForm.start_date} onChange={(e) => setEventForm({ ...eventForm, start_date: e.target.value })} required />
        </div>
        <div>
          <Label>End Date</Label>
          <Input type="date" value={eventForm.end_date} onChange={(e) => setEventForm({ ...eventForm, end_date: e.target.value })} required />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <Label>Attendee Entry Fee ($)</Label>
          <Input type="number" step="0.01" value={eventForm.buyer_fee} onChange={(e) => setEventForm({ ...eventForm, buyer_fee: parseFloat(e.target.value) || 0 })} required />
        </div>
        <div>
          <Label>Max Booths</Label>
          <Input type="number" value={eventForm.max_booths} onChange={(e) => setEventForm({ ...eventForm, max_booths: parseInt(e.target.value) || 100 })} required />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div>
          <Label>Basic Tier ($)</Label>
          <Input type="number" value={eventForm.booth_fees.basic} onChange={(e) => setEventForm({ ...eventForm, booth_fees: { ...eventForm.booth_fees, basic: parseInt(e.target.value) || 0 } })} />
        </div>
        <div>
          <Label>Premium Tier ($)</Label>
          <Input type="number" value={eventForm.booth_fees.premium} onChange={(e) => setEventForm({ ...eventForm, booth_fees: { ...eventForm.booth_fees, premium: parseInt(e.target.value) || 0 } })} />
        </div>
        <div>
          <Label>Elite Tier ($)</Label>
          <Input type="number" value={eventForm.booth_fees.elite} onChange={(e) => setEventForm({ ...eventForm, booth_fees: { ...eventForm.booth_fees, elite: parseInt(e.target.value) || 0 } })} />
        </div>
      </div>
      {/* Event Banner Upload */}
      <div>
        <Label>Event Banner</Label>
        <p className="text-xs text-slate-500 mb-2">Wide image recommended (1200x400px)</p>
        {eventId ? (
          <FileUpload
            entityType="event" entityId={eventId} mediaType="banner"
            currentUrl={eventForm.banner_url}
            onUploadSuccess={(url) => setEventForm({ ...eventForm, banner_url: url })}
            onRemove={async () => {
              try {
                await axios.delete(`${API}/upload/event/${eventId}/banner`);
                setEventForm({ ...eventForm, banner_url: '' });
                toast.success('Banner removed');
              } catch (e) { toast.error('Failed to remove banner'); }
            }}
            accept="image/*" label="Upload Banner" previewClassName="w-full aspect-[3/1]" fillWidth={true}
          />
        ) : (
          <div className="w-full aspect-[3/1] border-2 border-dashed border-slate-300 rounded-lg flex flex-col items-center justify-center bg-slate-50">
            <Image className="w-8 h-8 text-slate-400 mb-2" />
            <span className="text-sm text-slate-500">Banner can be added after creating the event</span>
          </div>
        )}
      </div>

      {/* Event Introduction Video */}
      <div className="border-t pt-4 mt-4">
        <Label className="text-base font-semibold mb-2 block">Event Introduction Video</Label>
        <p className="text-xs text-slate-500 mb-3">Video shown before "Join this Exhibit" section</p>
        
        {selectedEvent?.id ? (
          <div className="space-y-4">
            <div className="p-3 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-sm font-medium text-blue-800 mb-2 flex items-center gap-2">
                <span className="text-lg">🇺🇸</span> English Video (Default)
              </p>
              <VideoUpload
                uploadEndpoint={`/api/upload/event/${selectedEvent.id}/video`}
                deleteEndpoint={`/api/upload/event/${selectedEvent.id}/video`}
                currentUrl={eventForm.intro_video_url && !eventForm.intro_video_url.includes('youtube') ? eventForm.intro_video_url : null}
                onUploadSuccess={(url) => setEventForm({ ...eventForm, intro_video_url: url })}
                onDelete={() => setEventForm({ ...eventForm, intro_video_url: '' })}
                label="Upload English Video" maxSize={50}
              />
              <Input
                type="url" placeholder="Or paste YouTube URL..." className="mt-2"
                value={eventForm.intro_video_url?.includes('youtube') || eventForm.intro_video_url?.includes('youtu.be') ? eventForm.intro_video_url : ''}
                onChange={(e) => setEventForm({ ...eventForm, intro_video_url: e.target.value })}
              />
            </div>
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
              <p className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                <span className="text-lg">🇫🇷</span> French Video (Optional)
              </p>
              <Input type="url" placeholder="YouTube URL for French video..." value={eventForm.intro_video_url_fr || ''}
                onChange={(e) => setEventForm({ ...eventForm, intro_video_url_fr: e.target.value })} />
              {eventForm.intro_video_url_fr && <p className="text-xs text-green-600 mt-1">✓ French video configured</p>}
            </div>
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
              <p className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                <span className="text-lg">🇪🇸</span> Spanish Video (Optional)
              </p>
              <Input type="url" placeholder="YouTube URL for Spanish video..." value={eventForm.intro_video_url_es || ''}
                onChange={(e) => setEventForm({ ...eventForm, intro_video_url_es: e.target.value })} />
              {eventForm.intro_video_url_es && <p className="text-xs text-green-600 mt-1">✓ Spanish video configured</p>}
            </div>
            <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
              <p className="text-sm font-medium text-slate-700 mb-2 flex items-center gap-2">
                <span className="text-lg">🇩🇪</span> German Video (Optional)
              </p>
              <Input type="url" placeholder="YouTube URL for German video..." value={eventForm.intro_video_url_de || ''}
                onChange={(e) => setEventForm({ ...eventForm, intro_video_url_de: e.target.value })} />
              {eventForm.intro_video_url_de && <p className="text-xs text-green-600 mt-1">✓ German video configured</p>}
            </div>
          </div>
        ) : (
          <div className="space-y-2">
            <Label className="text-sm">Video URL (YouTube or direct link)</Label>
            <Input type="url" value={eventForm.intro_video_url}
              onChange={(e) => setEventForm({ ...eventForm, intro_video_url: e.target.value })}
              placeholder="https://www.youtube.com/watch?v=..." />
            <p className="text-xs text-slate-500">Multilingual videos can be added after creating the event</p>
          </div>
        )}
      </div>

      {/* List Pricing Section */}
      <div className="border-t pt-4 mt-4">
        <Label className="text-base font-semibold mb-2 block">Attendee & Exhibitor Lists Pricing</Label>
        <div className="grid grid-cols-2 gap-4 mb-4">
          <div>
            <Label>Attendee List Price ($)</Label>
            <Input type="number" step="0.01" value={eventForm.attendee_list_price} 
              onChange={(e) => setEventForm({ ...eventForm, attendee_list_price: parseFloat(e.target.value) || 0 })} />
          </div>
          <div>
            <Label>Exhibitor List Price ($)</Label>
            <Input type="number" step="0.01" value={eventForm.exhibitor_list_price} 
              onChange={(e) => setEventForm({ ...eventForm, exhibitor_list_price: parseFloat(e.target.value) || 0 })} />
          </div>
        </div>

        <div className="flex items-center space-x-2 mb-4">
          <input type="checkbox" id="use_tiered_pricing" checked={eventForm.use_tiered_pricing}
            onChange={(e) => setEventForm({ ...eventForm, use_tiered_pricing: e.target.checked })}
            className="rounded border-slate-300" />
          <Label htmlFor="use_tiered_pricing" className="text-sm">Enable tiered pricing based on list size</Label>
        </div>

        {eventForm.use_tiered_pricing && (
          <div className="space-y-4 ml-4 border-l-2 border-slate-200 pl-4">
            <div>
              <Label className="text-sm font-medium">Attendee List Tiers</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                <div><Label className="text-xs">Up to 100</Label>
                  <Input type="number" step="0.01" value={eventForm.tiered_pricing?.attendee_list?.[0]?.price || 0}
                    onChange={(e) => { const tp = { ...eventForm.tiered_pricing }; tp.attendee_list[0].price = parseFloat(e.target.value) || 0; setEventForm({ ...eventForm, tiered_pricing: tp }); }} /></div>
                <div><Label className="text-xs">Up to 500</Label>
                  <Input type="number" step="0.01" value={eventForm.tiered_pricing?.attendee_list?.[1]?.price || 0}
                    onChange={(e) => { const tp = { ...eventForm.tiered_pricing }; tp.attendee_list[1].price = parseFloat(e.target.value) || 0; setEventForm({ ...eventForm, tiered_pricing: tp }); }} /></div>
                <div><Label className="text-xs">Unlimited</Label>
                  <Input type="number" step="0.01" value={eventForm.tiered_pricing?.attendee_list?.[2]?.price || 0}
                    onChange={(e) => { const tp = { ...eventForm.tiered_pricing }; tp.attendee_list[2].price = parseFloat(e.target.value) || 0; setEventForm({ ...eventForm, tiered_pricing: tp }); }} /></div>
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium">Exhibitor List Tiers</Label>
              <div className="grid grid-cols-3 gap-2 mt-1">
                <div><Label className="text-xs">Up to 100</Label>
                  <Input type="number" step="0.01" value={eventForm.tiered_pricing?.exhibitor_list?.[0]?.price || 0}
                    onChange={(e) => { const tp = { ...eventForm.tiered_pricing }; tp.exhibitor_list[0].price = parseFloat(e.target.value) || 0; setEventForm({ ...eventForm, tiered_pricing: tp }); }} /></div>
                <div><Label className="text-xs">Up to 500</Label>
                  <Input type="number" step="0.01" value={eventForm.tiered_pricing?.exhibitor_list?.[1]?.price || 0}
                    onChange={(e) => { const tp = { ...eventForm.tiered_pricing }; tp.exhibitor_list[1].price = parseFloat(e.target.value) || 0; setEventForm({ ...eventForm, tiered_pricing: tp }); }} /></div>
                <div><Label className="text-xs">Unlimited</Label>
                  <Input type="number" step="0.01" value={eventForm.tiered_pricing?.exhibitor_list?.[2]?.price || 0}
                    onChange={(e) => { const tp = { ...eventForm.tiered_pricing }; tp.exhibitor_list[2].price = parseFloat(e.target.value) || 0; setEventForm({ ...eventForm, tiered_pricing: tp }); }} /></div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Early Bird Discount */}
      <div className="border-t pt-4 mt-4">
        <div className="flex items-center space-x-2 mb-4">
          <input type="checkbox" id="early_bird_enabled" checked={eventForm.early_bird_enabled}
            onChange={(e) => setEventForm({ ...eventForm, early_bird_enabled: e.target.checked })}
            className="rounded border-slate-300" />
          <Label htmlFor="early_bird_enabled" className="text-sm font-semibold">Enable Early Bird Discount</Label>
        </div>
        {eventForm.early_bird_enabled && (
          <div className="grid grid-cols-2 gap-4 ml-4 border-l-2 border-slate-200 pl-4">
            <div>
              <Label>Discount (%)</Label>
              <Input type="number" min="1" max="100" value={eventForm.early_bird_discount_percent} 
                onChange={(e) => setEventForm({ ...eventForm, early_bird_discount_percent: parseInt(e.target.value) || 15 })} />
            </div>
            <div>
              <Label>Days Before Event</Label>
              <Input type="number" min="1" value={eventForm.early_bird_days_before} 
                onChange={(e) => setEventForm({ ...eventForm, early_bird_days_before: parseInt(e.target.value) || 30 })} />
            </div>
          </div>
        )}
      </div>

      {/* Region Targeting */}
      <div className="border-t pt-4 mt-4">
        <Label className="text-base font-semibold mb-2 block">Region Targeting</Label>
        <select className="w-full border rounded-md p-2 text-sm" value={eventForm.region || ''}
          onChange={(e) => setEventForm({ ...eventForm, region: e.target.value })}>
          <option value="">Select a region...</option>
          <option value="us">🇺🇸 United States</option>
          <option value="ca">🇨🇦 Canada</option>
          <option value="fr">🇫🇷 France</option>
          <option value="de">🇩🇪 Germany</option>
          <option value="es">🇪🇸 Spain</option>
          <option value="gb">🇬🇧 United Kingdom</option>
          <option value="eu">🇪🇺 Europe</option>
          <option value="global">🌍 Global</option>
        </select>
      </div>

      {/* Promotional Coupon */}
      <div className="border-t pt-4 mt-4">
        <div className="flex items-center space-x-2 mb-4">
          <input type="checkbox" id="coupon_enabled" checked={eventForm.coupon_enabled}
            onChange={(e) => setEventForm({ ...eventForm, coupon_enabled: e.target.checked })}
            className="rounded border-slate-300" />
          <Label htmlFor="coupon_enabled" className="text-sm font-semibold">Enable Promotional Coupon</Label>
        </div>
        {eventForm.coupon_enabled && (
          <div className="space-y-3 ml-4 border-l-2 border-slate-200 pl-4">
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Coupon Code</Label>
                <Input value={eventForm.coupon_code} onChange={(e) => setEventForm({ ...eventForm, coupon_code: e.target.value.toUpperCase() })}
                  placeholder="e.g. EARLY20" /></div>
              <div><Label>Discount (%)</Label>
                <Input type="number" min="1" max="100" value={eventForm.coupon_discount_percent} 
                  onChange={(e) => setEventForm({ ...eventForm, coupon_discount_percent: parseInt(e.target.value) || 0 })} /></div>
              <div><Label>Expiration Date</Label>
                <Input type="date" value={eventForm.coupon_expiration_date} 
                  onChange={(e) => setEventForm({ ...eventForm, coupon_expiration_date: e.target.value })} /></div>
              <div><Label>Applies To</Label>
                <select className="w-full border rounded-md p-2 text-sm" value={eventForm.coupon_applies_to}
                  onChange={(e) => setEventForm({ ...eventForm, coupon_applies_to: e.target.value })}>
                  <option value="both">Both Attendees & Exhibitors</option>
                  <option value="attendees">Attendees Only</option>
                  <option value="exhibitors">Exhibitors Only</option>
                </select></div>
            </div>
            <div>
              <Label>Max Uses (leave empty for unlimited)</Label>
              <Input type="number" min="1" value={eventForm.coupon_max_uses || ''} 
                onChange={(e) => setEventForm({ ...eventForm, coupon_max_uses: e.target.value ? parseInt(e.target.value) : null })}
                placeholder="Unlimited" />
            </div>
          </div>
        )}
      </div>

      {/* Registration Stats Display */}
      <div className="border-t pt-4 mt-4">
        <div className="flex items-center space-x-2 mb-4">
          <input type="checkbox" id="show_registration_stats" checked={eventForm.show_registration_stats}
            onChange={(e) => setEventForm({ ...eventForm, show_registration_stats: e.target.checked })}
            className="rounded border-slate-300" />
          <Label htmlFor="show_registration_stats" className="text-sm font-semibold">Show Registration Statistics</Label>
        </div>
        {eventForm.show_registration_stats && (
          <div className="grid grid-cols-2 gap-4 ml-4 border-l-2 border-slate-200 pl-4">
            <div><Label>Attendee Threshold</Label>
              <Input type="number" min="0" value={eventForm.attendee_threshold} 
                onChange={(e) => setEventForm({ ...eventForm, attendee_threshold: parseInt(e.target.value) || 0 })} /></div>
            <div><Label>Exhibitor Threshold</Label>
              <Input type="number" min="0" value={eventForm.exhibitor_threshold} 
                onChange={(e) => setEventForm({ ...eventForm, exhibitor_threshold: parseInt(e.target.value) || 0 })} /></div>
          </div>
        )}
      </div>

      {/* Exhibitor Registration During Live */}
      <div className="border-t pt-4 mt-4">
        <div className="flex items-center space-x-2">
          <input type="checkbox" id="allow_exhibitor_registration_during_live"
            checked={eventForm.allow_exhibitor_registration_during_live}
            onChange={(e) => setEventForm({ ...eventForm, allow_exhibitor_registration_during_live: e.target.checked })}
            className="rounded border-slate-300" />
          <Label htmlFor="allow_exhibitor_registration_during_live" className="text-sm font-semibold">
            Allow Exhibitor Registration During Live Event
          </Label>
        </div>
      </div>

      <Button type="submit" className="w-full mt-4">{submitLabel}</Button>
    </form>
  );

  return (
    <>
      <div className="bg-white border border-slate-200 rounded-md p-4">
        <h2 className="text-lg font-bold text-slate-900 mb-4">{t('dashboard.manage_events')}</h2>
        {events.length === 0 ? (
          <div className="text-center py-8">
            <Calendar className="h-12 w-12 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-600">{t('dashboard.no_events')}</p>
          </div>
        ) : (
          <div className="space-y-3">
            {events.map((event) => (
              <div key={event.id} className="border border-slate-200 rounded-md p-4 hover:border-slate-300 transition-colors">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="font-semibold"><AutoTranslatedText text={event.name} /></h3>
                      {getStatusBadge(event.status)}
                    </div>
                    <p className="text-sm text-slate-600"><AutoTranslatedText text={event.description} /></p>
                    <p className="text-xs text-slate-500 mt-1">
                      {parseLocalDate(event.start_date).toLocaleDateString()} - {parseLocalDate(event.end_date).toLocaleDateString()}
                    </p>
                    <p className="text-xs text-slate-500">
                      {t('admin.buyer_fee')}: ${event.buyer_fee || 0} | {t('admin.booth')}: {t('admin.basic')} ${event.booth_fees?.basic || 0}, {t('admin.premium')} ${event.booth_fees?.premium || 0}, {t('admin.elite')} ${event.booth_fees?.elite || 0}
                    </p>
                  </div>
                  <div className="flex flex-col gap-2 ml-4">
                    <Select value={event.status} onValueChange={(val) => updateEventStatus(event.id, val)}>
                      <SelectTrigger className="w-40 h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="upcoming">{t('admin.upcoming')}</SelectItem>
                        <SelectItem value="registration_open">{t('admin.registration_open')}</SelectItem>
                        <SelectItem value="live">{t('admin.live')}</SelectItem>
                        <SelectItem value="completed">{t('admin.completed')}</SelectItem>
                      </SelectContent>
                    </Select>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => openEditEvent(event)} title="Edit">
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="destructive" onClick={() => { setEventToDelete(event); setShowDeleteEventConfirm(true); }} title="Delete">
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Edit Event Modal */}
      <Dialog open={showEditEventModal} onOpenChange={(open) => { setShowEditEventModal(open); if (!open) { setSelectedEvent(null); resetEventForm(); } }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>Edit Event</DialogTitle></DialogHeader>
          {renderEventForm(updateEvent, 'Update Event', selectedEvent?.id)}
        </DialogContent>
      </Dialog>

      {/* Delete Event Confirmation */}
      <Dialog open={showDeleteEventConfirm} onOpenChange={setShowDeleteEventConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Delete Event</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete <strong>{eventToDelete?.name}</strong>? This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="bg-amber-50 border border-amber-200 rounded-md p-4 text-sm">
            <p className="text-amber-800">
              <strong>Warning:</strong> Deleting this event will remove all associated booths and registrations. Refunds should be processed separately.
            </p>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteEventConfirm(false)}>Cancel</Button>
            <Button variant="destructive" onClick={deleteEvent}>Delete Event</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Event Modal */}
      <Dialog open={showCreateModal} onOpenChange={(open) => { setShowCreateModal(open); if (!open) resetEventForm(); }}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{t('dashboard.add_event')}</DialogTitle></DialogHeader>
          {renderEventForm(createEvent, t('dashboard.add_event'))}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default AdminEventsTab;
